import React, { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import Page from "../../types/Page";
import { apiService } from "../../common/api/apiService";
import { API_ENDPOINTS } from "../../common/api/apiEndpoints";
import { Button, Card, ListGroup, Spinner } from "react-bootstrap";
import { useAppDispatch } from "../../app/hooks";
import { getUserClaimsAsync } from "../../features/auth/authSlice";

const AccountSelectionPageType: Page = {
  ActiveTab: "Home",
  DisplayName: "Valitse tili",
  LinkUrl: "/select-account",
  Path: "/select-account",
  RouteName: "select-account",
};

interface AccountItem {
  id?: string;
  accountId?: string;
  caretakerNumber?: string | null;
  ownerNumber?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
}

const AccountSelectionPage = () => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await apiService.get(
          API_ENDPOINTS.IDENTITY.GET_USER_ACCOUNTS,
        );
        if (Array.isArray(data)) {
          if (data.length <= 1) {
            // No need to show selection page; if exactly one, preselect it
            if (data.length === 1) {
              const first = data[0];
              const id = first?.id ?? first?.accountId;
              if (id) {
                try {
                  await apiService.post(
                    API_ENDPOINTS.IDENTITY.SELECT_LOGIN_ACCOUNT,
                    JSON.stringify(id),
                  );
                  await dispatch(getUserClaimsAsync()).unwrap();
                  localStorage.setItem("accountSelectionDone", "true");
                } catch (e) {
                  // ignore, proceed to home
                }
              }
            }
            window.location.replace("/");
            return;
          }
          setAccounts(data);
        } else {
          setError("Palvelusta saatiin odottamaton vastaus.");
        }
      } catch (e) {
        setError("Tilien lataaminen epäonnistui.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dispatch]);

  const handleSelect = async (acc: AccountItem) => {
    const id = acc?.id ?? acc?.accountId;
    if (!id) return;
    try {
      setSaving(id);
      await apiService.post(
        API_ENDPOINTS.IDENTITY.SELECT_LOGIN_ACCOUNT,
        JSON.stringify(id),
      );
      await dispatch(getUserClaimsAsync()).unwrap();
      // Mark account selection as completed for this login session
      localStorage.setItem("accountSelectionDone", "true");
      window.location.replace("/");
    } catch (e) {
      setError("Tilin valinta epäonnistui.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className={AccountSelectionPageType.RouteName}>
      <Layout CurrentPage={AccountSelectionPageType}>
        <div className="container-narrow mx-auto">
          <h1 className="h3 mb-24">Valitse tili</h1>
          {loading && (
            <div className="d-flex gap-16 align-items-center">
              <Spinner animation="border" />
              <span>Ladataan tilejä…</span>
            </div>
          )}
          {error && <div className="alert alert-danger">{error}</div>}
          {!loading && accounts?.length > 1 && (
            <Card>
              <Card.Body>
                <p className="mb-16">
                  Sähköpostiosoitteellasi on useita tilejä. Valitse, millä
                  tilillä haluat kirjautua:
                </p>
                <ListGroup>
                  {accounts.map((acc, idx) => (
                    <ListGroup.Item
                      key={(acc.id ?? acc.accountId ?? idx).toString()}
                      className="d-flex flex-column flex-sm-row gap-8 justify-content-sm-between align-items-sm-center"
                    >
                      <div>
                        {acc.ownerNumber && (
                          <div className="fw-semibold">
                            Omistajanumero: {acc.ownerNumber}
                          </div>
                        )}
                        {acc.caretakerNumber && (
                          <div className="fw-semibold">
                            Huoltajanumero: {acc.caretakerNumber}
                          </div>
                        )}
                        <div className="fw-semibold">
                          {`${(acc.firstName ?? "").toString().trim()} ${(acc.lastName ?? "").toString().trim()}`.trim() ||
                            acc.companyName ||
                            "Tili"}
                        </div>
                      </div>
                      <Button
                        variant="primary"
                        disabled={!!saving}
                        onClick={() => handleSelect(acc)}
                      >
                        {saving === (acc.id ?? acc.accountId) ? (
                          <>
                            <Spinner
                              size="sm"
                              animation="border"
                              className="me-8"
                            />{" "}
                            Valitaan…
                          </>
                        ) : (
                          <>Valitse</>
                        )}
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          )}
        </div>
      </Layout>
    </div>
  );
};

export default AccountSelectionPage;
